import { AskRequest, AskRequestType } from '@hone-automation/common';
import { format } from 'date-fns';
import { mapAskRequestTypeToLabel } from 'presentation/components/KSQ2/Common/RequestType/RequestType';

// Type alias to avoid interface extension conflicts with AskRequest.priority
export type EnhancedAskRequest = AskRequest & {
  priority?: 'Blocker' | 'Normal';
};

export interface TransactionDetailsData {
  requestType: string;
  isAskRequest: boolean;
  isFlaggedTransaction: boolean;
  isFlaggedCell: boolean;
  isCustomRequest: boolean;
  createdDate: string;
  trxDate: string;
  createdByUser: string;
  updatedByUser: string;
  updatedDate: string;
  transactionName: string;
  transactionMemo: string;
  transactionAccount: string;
  transactionType: string;
  transactionAmount: string | number;
  flaggedAmount: string | number;
  hasBlockerPriority: boolean;
}

const toFormattedDate = (value?: string | Date | null): string => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'MM/dd/yyyy');
  } catch (error) {
    return '-';
  }
};

const getFirstComment = (request?: AskRequest | null) => {
  if (!request?.comments || request.comments.length === 0) {
    return undefined;
  }
  return request.comments[0];
};

const getLatestComment = (request?: AskRequest | null) => {
  if (!request?.comments || request.comments.length === 0) {
    return undefined;
  }
  return [...request.comments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
};

export const formatTransactionDetails = (
  request: EnhancedAskRequest | AskRequest | null | undefined
): TransactionDetailsData | null => {
  if (!request) {
    return null;
  }

  const requestType = mapAskRequestTypeToLabel[request.type] || request.type || 'Unknown';
  const isAskRequest = requestType.includes('Ask') || requestType.includes('Needs Information');
  const isFlaggedTransaction = requestType.includes('Flagged transaction');
  const isFlaggedCell = requestType.includes('Flagged Cell');
  const isCustomRequest = !isAskRequest && !isFlaggedTransaction && !isFlaggedCell;

  const createdDate = toFormattedDate(request.createdAt);

  const isoString = (request.transaction?.date as unknown as string) || undefined;
  const trxDate = isoString
    ? (() => {
        try {
          const [year, month, day] = isoString.split('T')[0].split('-');
          return `${month}/${day}/${year}`;
        } catch (error) {
          return '-';
        }
      })()
    : '-';

  const firstInteractionComment = getFirstComment(request);
  const createdByUser =
    firstInteractionComment?.user?.name ||
    firstInteractionComment?.userLocation?.email?.split('@')[0] ||
    firstInteractionComment?.userLocation?.role ||
    'Unknown';

  const latestStatus = getLatestComment(request);
  const updatedDate = toFormattedDate(latestStatus?.createdAt as string | Date | undefined);
  const updatedByUser =
    latestStatus?.user?.name ||
    latestStatus?.userLocation?.email?.split('@')[0] ||
    latestStatus?.userLocation?.role ||
    'Unknown';

  const transactionName = request.transaction?.name || '-';
  const transactionMemo = request.transaction?.memo || '-';
  const transactionAccount = request.transaction?.account || '--';
  const transactionAmount = request.transaction?.amount || '-';
  const flaggedAmount = request.flaggedAmount || transactionAmount;
  const transactionType = request.transaction?.externalTransactionType
    ? `${request.transaction?.externalTransactionType}${
        // EXPENSE transactions don't display reference numbers in QBO
        request.transaction?.externalTransactionType !== 'EXP' && request.transaction?.externalNum
          ? ` - ${request.transaction?.externalNum}`
          : ''
      }`
    : request.transaction?.externalNum || '-';

  const hasBlockerPriority = request.priority === 'Blocker';

  return {
    requestType,
    isAskRequest,
    isFlaggedTransaction,
    isFlaggedCell,
    isCustomRequest,
    createdDate,
    trxDate,
    createdByUser,
    updatedByUser,
    updatedDate,
    transactionName,
    transactionMemo,
    transactionAccount,
    transactionType: transactionType || '-',
    transactionAmount,
    flaggedAmount,
    hasBlockerPriority,
  };
};
